// src/components/ui/ModalCheckouts.jsx
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { format } from "date-fns";

function Cell({ children, className = "" }) {
  return <td className={`px-4 py-2 ${className}`}>{children}</td>;
}

function RowSkeleton() {
  return (
    <tr className="border-t border-slate-200 dark:border-slate-800">
      {Array.from({ length: 6 }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 w-full max-w-[10rem] animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        </td>
      ))}
    </tr>
  );
}

export default function ModalCheckouts({
  open,
  onClose,
  checkoutLink,      // objeto do link selecionado
  fetchCheckouts,    // função async(uuid) -> rows
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // carrega quando abrir
  useEffect(() => {
    let alive = true;
    async function load() {
      if (!open || !checkoutLink?.uuid) return;
      setLoading(true);
      try {
        const data = await fetchCheckouts(checkoutLink.uuid);
        if (!alive) return;
        setRows(Array.isArray(data) ? data : []);
      } catch {
        if (!alive) return;
        setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [open, checkoutLink, fetchCheckouts]);

  if (!open) return null;

  // helpers com fallback pra campos incertos do payload
  const getCountry = (r) => r.country || r.pais || r.country_name || r.country_code || "-";
  const getState   = (r) => r.state || r.estado || r.region || r.state_name || "-";
  const getCity    = (r) => r.city || r.cidade || r.town || r.locality || "-";
  const getKeyword = (r) => r.keyword || r.kw || r.search_keyword || r.search_term || "-";
  const getProduct = (r) => r.project_name || "-";
  const getDate = (r) => {
    const d = r.created_at || r.date || r.datetime || r.createdAt || r.timestamp;
    try { return d ? format(new Date(d), "dd/MM/yyyy HH:mm") : "-"; }
    catch { return "-"; }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 sm:p-6"
      aria-modal="true"
      role="dialog"
    >
      {/* overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* content */}
      <div className="relative mt-6 w-[min(96vw,1100px)] max-h-[85vh] overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl">
        {/* header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-200 dark:border-slate-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {checkoutLink?.name || checkoutLink?.platform || "Checkouts"}
          </h2>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <X size={20} />
          </button>
        </div>

        {/* table area */}
        <div className="overflow-auto max-h-[72vh]">
          <table className="min-w-full table-auto">
            <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200">
              <tr className="text-sm">
                <th className="px-4 py-2 text-left">Data/Hora</th>
                <th className="px-4 py-2 text-left">País</th>
                <th className="px-4 py-2 text-left">Estado</th>
                <th className="px-4 py-2 text-left">Cidade</th>
                <th className="px-4 py-2 text-left">Palavra-chave</th>
                <th className="px-4 py-2 text-left">Projeto</th>
              </tr>
            </thead>
            <tbody className="text-sm text-slate-800 dark:text-slate-200">
              {loading ? (
                <>
                  <RowSkeleton />
                  <RowSkeleton />
                  <RowSkeleton />
                </>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-10 text-center text-slate-500 dark:text-slate-400"
                  >
                    Nenhum checkout encontrado para este link.
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr
                    key={r.id || r.uuid || i}
                    className="border-t border-slate-200 dark:border-slate-800 hover:bg-blue-50/40 dark:hover:bg-slate-800/60 transition-colors"
                  >
                    <Cell>{getDate(r)}</Cell>
                    <Cell>{getCountry(r)}</Cell>
                    <Cell>{getState(r)}</Cell>
                    <Cell>{getCity(r)}</Cell>
                    <Cell>{getKeyword(r)}</Cell>
                    <Cell>{getProduct(r)}</Cell>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* footer (opcional) */}
        <div className="px-5 sm:px-6 py-3 border-t border-slate-200 dark:border-slate-800 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-slate-700 dark:text-slate-200 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
